import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Badge, Card as RBCard } from 'react-bootstrap';
import PropTypes from 'prop-types';
import Icon from './Icon';
import { Col, Row } from './Grid';
import { useCollapseManager } from './CardCollapseScope';
import ReactForm from './ReactForm';
import { useTranslation } from 'react-i18next';
/**
 * General purpose Bootstrap Card component.
 * 
 * The components supports collapsibility and abstracts the markup for titles.
 * @component
 */
const BaseCard = forwardRef(({ id, title, children, defaultCollapsed = false, fixed = false, cardStyle, headerStyle, bodyStyle, headerControls, footerControls, footerStyle, invalid = false, expandOnInvalid = true, unmountOnCollapse = false }, ref) => {
    const [collapsed, setCollapsed] = useState(!!defaultCollapsed);
    const { t } = useTranslation();
    const collapseManager = useCollapseManager();
    const showInvalid = !!invalid;
    const formRef = useRef(null);

    useImperativeHandle(ref, () => ({
        checkValidity: () => formRef.current?.checkValidity?.(),
        reset: () => formRef.current?.reset?.()
    }), []);

    useEffect(() => {
        return collapseManager?.register?.(setCollapsed, collapsed);
    }, [collapseManager, collapsed]);

    useEffect(() => {
        if (!expandOnInvalid || !showInvalid || fixed || !collapsed) {
            return;
        }
        setCollapsed(false);
    }, [expandOnInvalid, showInvalid, fixed, collapsed]);

    // Unmounted children do not participate in validation to prioritise performance
    // We check the config to determine if we should render children and if they should be visible.
    const renderBody = !unmountOnCollapse || !collapsed;
    const showBody = !collapsed;

    const body = <ReactForm ref={formRef} registerAsChild>{children}</ReactForm>;
    const headerStyles = useMemo(() => ({
        cursor: fixed ? null : 'pointer',
        fontWeight: 'bold',
        fontSize: 'larger',
        ...(showInvalid ? { borderColor: '#dc3545', boxShadow: '0 0 0 1px rgba(220,53,69,0.4)' } : null),
        ...headerStyle
    }), [fixed, showInvalid, headerStyle]);

    return <RBCard id={id} className='mx-1 my-3' style={{ borderRadius: '8px', ...cardStyle }}>
        { title ? <RBCard.Header style={headerStyles} onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!fixed) {
                setCollapsed(!collapsed);
            }
        }}>
            <Row className="align-items-center">
                {fixed ? null : <Col xs='auto' className="mx-2 d-flex align-items-center"><Icon intent={collapsed ? 'expand' : 'collapse'}></Icon></Col>} 
                <Col className="d-flex align-items-center">{title}</Col>
                {showInvalid && <Col xs='auto'>
                    <Badge variant="danger" aria-label={t('fieldInvalid_label')}>
                        <Icon variant="solid" kind="triangle-exclamation"></Icon>
                        {t('fieldInvalid_label')}
                    </Badge>
                </Col>}
                {headerControls && <Col xs='auto' className="text-end d-flex align-items-center" onClick={e => e.stopPropagation()}>
                    {headerControls}
                </Col>}
            </Row>
        </RBCard.Header> : null}
        { renderBody ? <RBCard.Body style={{ ...bodyStyle, ...(!showBody ? { display: 'none' } : null)}} hidden={!showBody}>
            {body}
        </RBCard.Body> : null}
        {footerControls ? <RBCard.Footer style={{backgroundColor: 'inherit', ...footerStyle} }>{footerControls}</RBCard.Footer> : null}
    </RBCard>
});

BaseCard.propTypes = {
    /**
     * Components that make up the title of the card, if not supplied no header will be rendered
     */
    title: PropTypes.node,
    /**
     * Components that make up the body of the card
     */
    children: PropTypes.node.isRequired,
    /**
     * The initial collapse state of the card
     */
    defaultCollapsed: PropTypes.bool,
    /**
     * Option to disable the card capability to be collapsed
     */
    fixed: PropTypes.bool,
    /**
     * A React Node which are the controls to render in the card header
     */
    headerControls: PropTypes.node,
    /**
     * A React Node which are the controls to render in the card footer
     */
    footerControls: PropTypes.node,
    /**
     * A style object to be applied to the card footer
     */
    footerStyle: PropTypes.object,
    /**
     * A style object to be applied to the card header
     */
    headerStyle: PropTypes.object,
    /**
     * A style object to be applied to the card body
     */
    bodyStyle: PropTypes.object,
    /**
     * A style object to be applied to the entire card
     */
    cardStyle: PropTypes.object,
    /**
     * Indicates the card body contains invalid controls.
     */
    invalid: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
    /**
     * Label shown alongside the invalid icon when invalid is true.
     */
    invalidLabel: PropTypes.node,
    /**
     * Expands the card when invalid is true.
     */
    expandOnInvalid: PropTypes.bool,
    /**
     * When true, unmount the body on collapse for performance.
     */
    unmountOnCollapse: PropTypes.bool,
}

export const Card = forwardRef(({ ...props }, ref) => <BaseCard ref={ref} {...props} unmountOnCollapse={false}></BaseCard>);
// Unmounts children on collapse to improve performance, but excludes them from invalidation/validation until expanded.
export const EphemeralCard = forwardRef(({ ...props }, ref) => <BaseCard ref={ref} {...props} unmountOnCollapse={true}></BaseCard>);

export default Card;